import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Beef, CalendarDays, ChevronRight, Tag } from 'lucide-react'
import PageHeader from '../../components/PageHeader'
import DataLoadError from '../../components/DataLoadError'
import MediaThumb from '../../components/MediaThumb'
import DeltaBadge from '../../components/WeightDelta'
import StarButton from '../../components/StarButton'
import { getKraalFeed, getOwnerSegments } from '../../services/api'
import { EXIT_LABEL, feedHeadline, fmtN, formatDay, formatEAT, formatKg, pctText, weightDelta } from '../../utils/presence'

// The kraal feed (plan smartkraal-owner-experience B2): "your animals this week".
// Each kraal day in the window, newest first, with the day's headline and a
// strip of the animals the kraal identified; births and exits below. The server
// scopes the strips to the caller's own animals when their account is linked to
// an owner segment and always sends the ranch-wide line next to it, so a
// partial session reads as the gap it is (decision 1).

export const WINDOWS = [{ days: 7, label: '7 days' }, { days: 28, label: '28 days' }, { days: 90, label: '90 days' }]

/** One animal on a day's strip: the day's still (the display photo when the still
 *  has no thumbnail yet), the "New" badge for a first visit, the weight with its
 *  change against the previous weighing, and the star (a sibling of the link, so
 *  the tile's link holds no button). */
export function FeedTile({ ranchId, animal }) {
  const [useHero, setUseHero] = useState(false)
  const still = animal.still?.thumb || null
  const src = (!useHero && still) || animal.hero?.thumb || null
  const delta = animal.weighed ? weightDelta(animal.weight_delta_kg) : null
  return (
    <div className="relative w-32 shrink-0 snap-start sm:w-36" data-testid="feed-tile" title={animal.name}>
      <Link to={`/cattle/${animal.id}`}
        className="group block overflow-hidden rounded-xl border border-earth-200 bg-white shadow-sm transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500">
        <div className="relative">
          <MediaThumb src={src} alt={animal.name} className="aspect-[4/3] w-full"
                      style={{ backgroundColor: animal.hero?.dominant_rgb || '#e8e2d6' }}
                      onFail={() => { if (!useHero && still && animal.hero?.thumb) setUseHero(true) }}
                      glyph={<Beef className="h-8 w-8 text-black/20" aria-hidden="true" />} />
          {animal.first_time_at_kraal && (
            <span className="absolute left-1.5 top-1.5 rounded-full bg-amber-400 px-2 py-0.5 text-[11px] font-semibold text-amber-950 shadow-sm"
                  title="First time at the kraal" data-testid="feed-new">New</span>
          )}
        </div>
        <div className="p-2">
          <div className="truncate text-sm font-medium text-gray-900">{animal.name}</div>
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-earth-600">
            {animal.weighed
              ? <span className="font-medium text-gray-900">{formatKg(animal.weight_kg)} kg</span>
              : <span className="text-earth-400">read, not weighed</span>}
            {delta && <DeltaBadge delta={delta} />}
          </div>
        </div>
      </Link>
      <StarButton ranchId={ranchId} animalId={animal.id} name={animal.name} className="absolute right-1 top-1" />
    </div>
  )
}

/** Where "+N more" can point and mean exactly that day's animals: the cattle list
 *  filtered on the last dip day (carrying the one segment in scope); an older day
 *  has no such list, so the count stays plain text. */
function moreLink(day, lastDipDay, segments) {
  if (day.day !== lastDipDay) return null
  const seg = segments?.length === 1 && segments[0].id ? `&owner_segment_id=${segments[0].id}` : ''
  return `/cattle?seen=last_dip${seg}`
}

function DayCard({ ranchId, day, lastDipDay, segments }) {
  const more = Math.max(0, (day.animals_total || 0) - (day.animals || []).length)
  const moreTo = moreLink(day, lastDipDay, segments)
  return (
    <section className="card p-4 sm:p-5" data-testid="feed-day" data-dip={day.is_dip_day ? '1' : '0'}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-gray-900">{formatDay(day.day)}</h2>
          {day.is_dip_day
            ? <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800">Dip day</span>
            : <span className="rounded-full bg-earth-100 px-2 py-0.5 text-xs font-medium text-earth-600" title="Fewer tags than a dip day: shown, not scored">Test pass</span>}
        </div>
        <p className="text-sm text-earth-600">
          <span className="font-semibold text-gray-900" data-testid="feed-headline">{feedHeadline(day)}</span>
          {day.mine
            ? <>{day.mine.animals ? ` (${pctText(day.mine.seen, day.mine.animals)})` : ''} · Ranch: {fmtN(day.seen)} of {fmtN(day.herd)}{day.herd ? ` (${pctText(day.seen, day.herd)})` : ''}</>
            : <>{day.herd ? ` (${pctText(day.seen, day.herd)})` : ''} · {fmtN(day.tags_read)} tags read</>}
        </p>
      </div>
      {(day.animals || []).length > 0 ? (
        <div className="mt-3 -mx-1 flex snap-x gap-3 overflow-x-auto px-1 pb-2" data-testid="feed-strip">
          {day.animals.map((a) => <FeedTile key={a.id} ranchId={ranchId} animal={a} />)}
          {more > 0 && (moreTo ? (
            <Link to={moreTo} data-testid="feed-more"
              className="snap-start flex w-28 shrink-0 flex-col items-center justify-center rounded-xl border border-dashed border-earth-300 text-sm text-earth-600 hover:border-primary-300 hover:text-primary-700">
              <span className="text-lg font-semibold text-gray-900">+{fmtN(more)}</span>
              <span>more</span>
            </Link>
          ) : (
            <div data-testid="feed-more" title="The strip shows the first tiles; the day's full count is here"
              className="snap-start flex w-28 shrink-0 flex-col items-center justify-center rounded-xl border border-dashed border-earth-200 text-sm text-earth-500">
              <span className="text-lg font-semibold text-gray-900">+{fmtN(more)}</span>
              <span>more that day</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-sm text-earth-500">
          {day.mine ? 'None of your animals were identified that day.' : 'No registered animal was identified that day.'}
        </p>
      )}
    </section>
  )
}

function Face({ hero, name }) {
  return (
    <MediaThumb src={hero?.thumb || null} alt={name} className="h-10 w-10 shrink-0 rounded-lg bg-earth-100"
                style={{ backgroundColor: hero?.dominant_rgb || undefined }}
                glyph={<Beef className="h-5 w-5 text-earth-300" aria-hidden="true" />} />
  )
}

function Movements({ data }) {
  const births = data.births || []
  const exits = data.exits || []
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <section className="card p-4 sm:p-5" data-testid="feed-births">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">Born in the last {data.days} days</h2>
        {births.length === 0 ? (
          <p className="mt-2 text-sm text-earth-500">No births recorded.</p>
        ) : (
          <ul className="mt-3 divide-y divide-earth-100">
            {births.map((b) => {
              const calf = b.calf
              const to = `/cattle/${calf ? calf.id : b.dam.id}`
              return (
                <li key={b.event_id}>
                  <Link to={to} className="flex items-center gap-3 py-2 hover:bg-earth-50">
                    <Face hero={calf?.hero} name={calf?.name || b.dam.name} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {calf ? calf.name : (b.calf_name_recorded || 'Calf')}
                        <span className="font-normal text-earth-500"> · dam {b.dam.name}</span>
                      </p>
                      <p className="text-xs text-earth-500">
                        {formatDay(b.date)}{b.verification_status === 'pending' ? ' · awaiting approval' : ''}
                        {!calf ? ' · not yet registered' : ''}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-earth-400" />
                  </Link>
                </li>
              )
            })}
          </ul>
        )}
      </section>
      <section className="card p-4 sm:p-5" data-testid="feed-exits">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700">Left the herd in the last {data.days} days</h2>
        {exits.length === 0 ? (
          <p className="mt-2 text-sm text-earth-500">No deaths, sales or losses recorded.</p>
        ) : (
          <ul className="mt-3 divide-y divide-earth-100">
            {exits.map((x) => (
              <li key={x.event_id}>
                <Link to={`/cattle/${x.animal.id}`} className="flex items-center gap-3 py-2 hover:bg-earth-50">
                  <Face hero={x.animal.hero} name={x.animal.name} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {x.animal.name}
                      <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${x.event_type === 'sale' ? 'bg-blue-50 text-blue-800' : 'bg-red-50 text-red-700'}`}>
                        {EXIT_LABEL[x.event_type] || x.event_type}
                      </span>
                    </p>
                    <p className="text-xs text-earth-500">
                      {formatDay(x.date)}{x.cause ? ` · ${x.cause}` : ''}{x.verification_status === 'pending' ? ' · awaiting approval' : ''}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-earth-400" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

export default function KraalFeed({ ranchId }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const daysParam = Number(searchParams.get('days')) || 28
  const days = WINDOWS.some((w) => w.days === daysParam) ? daysParam : 28
  const segment = searchParams.get('segment') || ''
  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next, { replace: true })
  }
  const params = { days, ...(segment ? { segment } : {}) }
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['kraalFeed', ranchId, params],
    queryFn: ({ signal }) => getKraalFeed(ranchId, params, { signal }),
    enabled: !!ranchId,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  })
  // A manager (or an owner with no linked segment) may read one owner's feed; an
  // account linked to owner segments sees its own and gets no picker (the server
  // refuses any other segment).
  const canPick = !!data?.configured && !data.linked
  const { data: segments } = useQuery({
    queryKey: ['ownerSegments', ranchId, 'all'],
    queryFn: () => getOwnerSegments(ranchId, { includeEmpty: true }),
    enabled: !!ranchId && canPick,
    staleTime: 10 * 60 * 1000,
  })

  const scopeText = !data?.configured ? ''
    : data.scope === 'mine' ? `Your animals${data.segments?.length ? ` (${data.segments.map((s) => s.name).join(', ')})` : ''}`
      : data.scope === 'segment' ? `${data.segments?.[0]?.name || 'One owner'}'s animals`
        : 'The whole herd'
  const latestDip = (data?.kraal_days || []).find((d) => d.is_dip_day)
  // Every dip day is a card, even one with none of the reader's animals (the kraal
  // ran; they were not there). A test pass that read none of them is noise.
  const shownDays = (data?.kraal_days || []).filter((d) => d.is_dip_day || (d.animals || []).length > 0)
  const now = data?.mine_now || data?.herd_now
  const unbound = now ? Math.max(0, (now.animals || 0) - (now.bound || 0)) : 0

  return (
    <div className="space-y-6">
      <Link to="/cattle" className="inline-flex items-center text-earth-500 hover:text-earth-700">
        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Cattle
      </Link>
      <PageHeader title="At the kraal" subtitle={scopeText ? `${scopeText} · the last ${days} days` : 'Who the kraal saw, day by day'} icon={CalendarDays} />

      <div className="flex flex-wrap items-center gap-2" data-testid="feed-controls">
        <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-earth-500">Window</span>
        {WINDOWS.map((w) => (
          <button key={w.days} type="button" onClick={() => setParam('days', w.days === 28 ? '' : String(w.days))}
            aria-pressed={days === w.days} data-testid={`feed-window-${w.days}`}
            className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${days === w.days
              ? 'border-primary-600 bg-primary-600 text-white'
              : 'border-earth-200 bg-white text-earth-700 hover:bg-earth-50'}`}>
            {w.label}
          </button>
        ))}
        {canPick && (segments || []).length > 0 && (
          <select value={segment} onChange={(e) => setParam('segment', e.target.value)} data-testid="feed-segment"
            className="ml-auto rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500">
            <option value="">Whole ranch</option>
            {segments.filter((s) => s.id && s.name).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
      </div>

      {isPending ? (
        <div className="space-y-4" aria-busy="true" data-testid="feed-placeholder">
          <div className="card min-h-[7rem] animate-pulse" />
          <div className="card min-h-[14rem] animate-pulse" />
          <div className="card min-h-[14rem] animate-pulse" />
        </div>
      ) : error ? (
        <DataLoadError error={error} title="Failed to load the kraal feed" onRetry={() => refetch()} />
      ) : !data?.configured ? (
        <div className="card p-8 text-center">
          <Beef className="mx-auto mb-3 h-10 w-10 text-earth-300" />
          <p className="text-gray-700">This ranch has no SmartKraal kraal yet, so there is no feed to show.</p>
        </div>
      ) : (
        <>
          {/* At a glance: the newest dip day in the window and the tag gap */}
          <div className="card p-4 sm:p-5" data-testid="feed-glance">
            {latestDip ? (
              <>
                <p className="text-sm text-earth-500">Last dip day in this window</p>
                <p className="mt-0.5 text-xl font-semibold text-gray-900">
                  {formatDay(latestDip.day)} · {feedHeadline(latestDip)}
                  {latestDip.mine
                    ? (latestDip.mine.animals ? ` (${pctText(latestDip.mine.seen, latestDip.mine.animals)})` : '')
                    : (latestDip.herd ? ` (${pctText(latestDip.seen, latestDip.herd)})` : '')}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-earth-500">No dip day in the last {days} days</p>
                <p className="mt-0.5 text-base text-gray-900">
                  {data.last_dip_day ? `The last dip day was ${formatDay(data.last_dip_day)}.` : 'The kraal has not read the herd yet.'}
                </p>
              </>
            )}
            <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-earth-600">
              {latestDip?.mine && <span>Ranch: {fmtN(latestDip.seen)} of {fmtN(latestDip.herd)} seen{latestDip.herd ? ` (${pctText(latestDip.seen, latestDip.herd)})` : ''}</span>}
              {unbound > 0 && (
                <span className="inline-flex items-center gap-1" title="No SmartKraal tag is bound: the kraal cannot see these animals">
                  <Tag className="h-3.5 w-3.5 text-earth-400" />
                  {fmtN(unbound)} {data.mine_now ? 'of your animals have' : 'animals have'} no tag
                </span>
              )}
              {(data.births?.length || data.exits?.length) ? (
                <span>
                  {data.births?.length ? `${fmtN(data.births.length)} born` : ''}
                  {data.births?.length && data.exits?.length ? ' · ' : ''}
                  {data.exits?.length ? `${fmtN(data.exits.length)} left the herd` : ''}
                </span>
              ) : null}
              {data.data_as_of_utc && <span className="text-xs text-earth-400">Kraal data as of {formatEAT(data.data_as_of_utc)}</span>}
            </p>
          </div>

          {shownDays.length === 0 ? (
            <div className="card p-8 text-center" data-testid="feed-empty">
              <CalendarDays className="mx-auto mb-3 h-10 w-10 text-earth-300" />
              <p className="text-gray-700">
                {(data.kraal_days || []).length
                  ? `No dip day in the last ${days} days, and the test passes read none of ${data.scope === 'ranch' ? 'the registered' : 'your'} animals.`
                  : `The kraal read no tags in the last ${days} days.`}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {shownDays.map((d) => (
                <DayCard key={d.day} ranchId={ranchId} day={d} lastDipDay={data.last_dip_day} segments={data.segments} />
              ))}
            </div>
          )}

          <Movements data={data} />
        </>
      )}
    </div>
  )
}
